import React, { useState, useEffect, useRef } from "react";
import { setupMfa, verifySetup, resendSetupCode } from "../../services/mfaService";
import MfaModal from "./MfaModal";

interface MfaSetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

const RESEND_COOLDOWN_SECONDS = 30;

const MfaSetup: React.FC<MfaSetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 2) {
      codeInputRef.current?.focus();
    }
  }, [step]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await setupMfa(phoneNumber);
      setStep(2);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes.");
      } else {
        setError(err.response?.data?.error || "Failed to send verification code.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await verifySetup(code);
      setStep(3);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes.");
      } else {
        setError(err.response?.data?.error || "Incorrect code. Please try again.");
      }
      setCode("");
      codeInputRef.current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendSetupCode();
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setError("");
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError("Too many attempts. Please wait a few minutes.");
      } else {
        setError(err.response?.data?.error || "Failed to resend code.");
      }
    }
  };

  // Step 1: Phone + Password
  if (step === 1) {
    return (
      <MfaModal onClose={onCancel}>
        <h2 className="mfa-title">Enable Two-Factor Authentication</h2>
        <p className="mfa-subtitle">
          To continue, enter your phone number.
        </p>

        <form onSubmit={handleSendCode} className="mfa-form">
          <div className="mfa-field">
            <label htmlFor="phone-number" className="mfa-label">
              Phone Number
            </label>
            <input
              id="phone-number"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+15551234567"
              className="mfa-input"
              required
            />
          </div>

          <label className="mfa-checkbox-row">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mfa-checkbox"
            />
            <span className="mfa-checkbox-text">
              By pressing "Continue", I agree to receive Two-Factor Authentication
              messages to the provided mobile number. Message & data rates may apply.
            </span>
          </label>

          {error && (
            <div className="mfa-alert mfa-alert--error">
              <p>{error}</p>
            </div>
          )}

          <div className="mfa-actions">
            <button
              type="button"
              onClick={onCancel}
              className="mfa-btn mfa-btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !phoneNumber || !consentChecked}
              className="mfa-btn mfa-btn--primary"
            >
              {isLoading ? "Sending..." : "Continue"}
            </button>
          </div>
        </form>
      </MfaModal>
    );
  }

  // Step 2: Code Verification
  if (step === 2) {
    return (
      <MfaModal onClose={onCancel}>
        <h2 className="mfa-title">Enable Two-Factor Authentication</h2>
        <p className="mfa-subtitle">
          To finish enabling two-factor authentication, enter the code sent to{" "}
          <span className="mfa-phone-highlight">{phoneNumber}</span>.
        </p>

        <form onSubmit={handleVerifyCode} className="mfa-form">
          <div className="mfa-field">
            <label htmlFor="setup-code" className="mfa-label">
              Code
            </label>
            <input
              ref={codeInputRef}
              id="setup-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="mfa-input mfa-input--code"
            />
          </div>

          {error && (
            <div className="mfa-alert mfa-alert--error">
              <p>{error}</p>
            </div>
          )}

          <div className="mfa-inline-row">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="mfa-link"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
            <button
              type="button"
              onClick={() => { setStep(1); setCode(""); setError(""); }}
              className="mfa-link"
            >
              Use a different number
            </button>
          </div>

          <div className="mfa-actions">
            <button
              type="button"
              onClick={onCancel}
              className="mfa-btn mfa-btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              className="mfa-btn mfa-btn--primary"
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>
          </div>
        </form>
      </MfaModal>
    );
  }

  // Step 3: Success
  return (
    <MfaModal>
      <div className="mfa-success">
        <div className="mfa-success-icon">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="mfa-title">Two-Factor Authentication Enabled</h2>
        <p className="mfa-description">
          Verification codes will be sent to ****{phoneNumber.slice(-4)} when you log in.
        </p>
        <button onClick={onComplete} className="mfa-btn mfa-btn--primary">
          Done
        </button>
      </div>
    </MfaModal>
  );
};

export default MfaSetup;
